import React, { useState, useEffect } from 'react';
import syncManager from '../utils/syncManager';
import { getSyncStatus } from '../utils/db';

function SyncStatus() {
  const [status, setStatus] = useState({
    online: navigator.onLine,
    syncing: false,
    hasUnsynced: false,
    unsyncedCount: 0,
    progress: 0
  });

  useEffect(() => {
    // Check for unsynced data on mount
    checkUnsyncedData();

    // Listen for sync events
    const handleSyncStatus = (syncStatus) => {
      setStatus(prev => ({
        ...prev,
        ...syncStatus
      }));
    };

    syncManager.addSyncListener(handleSyncStatus);

    // Listen for online/offline events
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, online: true }));
      // Auto-sync when coming back online (with delay to prevent race conditions)
      setTimeout(() => {
        syncManager.syncAll();
      }, 1000);
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, online: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      syncManager.removeSyncListener(handleSyncStatus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkUnsyncedData = async () => {
    const syncStatus = await getSyncStatus();
    setStatus(prev => ({
      ...prev,
      hasUnsynced: syncStatus.hasUnsyncedData,
      unsyncedCount: syncStatus.unsyncedCount
    }));
  };

  const handleManualSync = async () => {
    console.log('🔘 SYNC BUTTON CLICKED');
    const result = await syncManager.syncAll();
    console.log('📊 SYNC RESULT:', result);
    if (result.success) {
      // Recheck unsynced data after successful sync
      await checkUnsyncedData();
    }
  };

  const handleDismiss = () => {
    console.log('❌ DISMISS CLICKED');
    setStatus({
      online: navigator.onLine,
      syncing: false,
      hasUnsynced: false,
      unsyncedCount: 0,
      progress: 0,
      dismissed: true
    });
  };

  // Don't show anything if dismissed, or if online and no unsynced data
  if (status.dismissed || (status.online && !status.hasUnsynced && !status.syncing)) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-50 max-w-sm">
      <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg p-4 relative">
        {/* Close button */}
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDismiss();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDismiss();
          }}
          className="absolute top-1 right-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold"
          style={{ touchAction: 'manipulation' }}
        >
          ×
        </button>

        {/* Offline Indicator */}
        {!status.online && (
          <div className="flex items-center space-x-2 text-orange-600">
            <span className="text-2xl">📴</span>
            <div>
              <div className="font-bold">Offline Mode</div>
              <div className="text-sm text-gray-600">
                Your data will sync when you're back online
              </div>
            </div>
          </div>
        )}

        {/* Syncing Indicator */}
        {status.syncing && (
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="animate-spin text-2xl">⟳</div>
            <div>
              <div className="font-bold">Syncing...</div>
              {status.progress > 0 && (
                <div className="text-sm text-gray-600">
                  {Math.round(status.progress)}% complete
                  {status.current && status.total && (
                    <span> ({status.current}/{status.total})</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Unsynced Data Indicator */}
        {status.online && status.hasUnsynced && !status.syncing && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-yellow-600">
              <span className="text-2xl">⚠️</span>
              <div>
                <div className="font-bold">
                  {status.unsyncedCount} {status.unsyncedCount === 1 ? 'Entry' : 'Entries'} Not Synced
                </div>
                <div className="text-sm text-gray-600">
                  Click to sync your offline data
                </div>
              </div>
            </div>
            <button
              onClick={handleManualSync}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Sync Now
            </button>
          </div>
        )}

        {/* Sync Complete */}
        {status.complete && (
          <div className="flex items-center space-x-2 text-green-600">
            <span className="text-2xl">✅</span>
            <div>
              <div className="font-bold">Sync Complete</div>
              <div className="text-sm text-gray-600">
                All data synced successfully
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {status.error && (
          <div className="flex items-center space-x-2 text-red-600">
            <span className="text-2xl">❌</span>
            <div>
              <div className="font-bold">Sync Error</div>
              <div className="text-sm text-gray-600">
                {status.error}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SyncStatus;